import React, { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Coins, AlertCircle, Sparkles, Search, ChevronRight, Activity, PieChart as PieChartIcon, BarChart3, ShieldAlert, AlertTriangle, ArrowUpRight, Zap, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';
import { MiniSparkline } from '../components/charts/MiniSparkline';
// --- MOCK DATA ---
const generateDailyCosts = () => {
  const data = [];
  const today = new Date();
  for (let i = 30; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short'
      }),
      OpenAI: Math.floor(Math.random() * 50) + 80,
      Anthropic: Math.floor(Math.random() * 30) + 40,
      Google: Math.floor(Math.random() * 20) + 15
    });
  }
  return data;
};
const DAILY_COSTS = generateDailyCosts();
const SERVICES_DATA = [{
  id: 's1',
  name: 'Customer Assistant',
  provider: 'OpenAI',
  model: 'GPT-5.4',
  requests: '124.5K',
  tokens: '45.2M',
  cost: 1245.5,
  trend: [40, 45, 55, 60, 75, 85, 90],
  status: 'warning'
}, {
  id: 's2',
  name: 'Smart Matcher',
  provider: 'Anthropic',
  model: 'Claude Sonnet 4.6',
  requests: '85.2K',
  tokens: '28.4M',
  cost: 840.2,
  trend: [60, 58, 55, 50, 48, 45, 42],
  status: 'success'
}, {
  id: 's3',
  name: 'Content Moderator',
  provider: 'OpenAI',
  model: 'GPT-5.4 mini',
  requests: '210.8K',
  tokens: '15.1M',
  cost: 320.8,
  trend: [20, 22, 25, 24, 26, 28, 30],
  status: 'success'
}, {
  id: 's4',
  name: 'AI Tutor',
  provider: 'Anthropic',
  model: 'Claude Haiku 4.5',
  requests: '45.1K',
  tokens: '12.5M',
  cost: 180.4,
  trend: [15, 18, 16, 20, 19, 22, 25],
  status: 'success'
}, {
  id: 's5',
  name: 'Resume Screener',
  provider: 'OpenAI',
  model: 'GPT-5.4',
  requests: '12.4K',
  tokens: '8.2M',
  cost: 650.0,
  trend: [80, 85, 90, 95, 100, 110, 120],
  status: 'danger'
}, {
  id: 's6',
  name: 'Document Analyzer',
  provider: 'Google',
  model: 'Gemini 3.1 Pro',
  requests: '32.6K',
  tokens: '18.4M',
  cost: 420.6,
  trend: [30, 32, 35, 34, 36, 38, 40],
  status: 'success'
}, {
  id: 's7',
  name: 'Chatbot HR',
  provider: 'OpenAI',
  model: 'GPT-5.4 mini',
  requests: '56.2K',
  tokens: '5.8M',
  cost: 110.2,
  trend: [10, 12, 11, 10, 12, 14, 15],
  status: 'success'
}, {
  id: 's8',
  name: 'Email Classifier',
  provider: 'Google',
  model: 'Gemini 3.1 Flash-Lite',
  requests: '180.5K',
  tokens: '9.2M',
  cost: 85.5,
  trend: [5, 6, 5, 7, 6, 8, 7],
  status: 'success'
}].sort((a, b) => b.cost - a.cost);
const PROVIDER_DATA = [{
  name: 'OpenAI',
  value: 58,
  color: '#00B4D8',
  cost: 2326.5,
  models: 3,
  topModel: 'GPT-5.4',
  trend: '+15%'
}, {
  name: 'Anthropic',
  value: 28,
  color: '#2A3B5C',
  cost: 1020.6,
  models: 2,
  topModel: 'Claude Sonnet 4.6',
  trend: '-5%'
}, {
  name: 'Google',
  value: 14,
  color: '#10b981',
  cost: 506.1,
  models: 2,
  topModel: 'Gemini 3.1 Pro',
  trend: '+2%'
}];
const TOKEN_COST_DATA = [{
  name: 'GPT-5.4',
  cost: 30.0,
  fill: '#00B4D8'
}, {
  name: 'Claude Sonnet 4.6',
  cost: 15.0,
  fill: '#2A3B5C'
}, {
  name: 'Gemini 3.1 Pro',
  cost: 7.0,
  fill: '#10b981'
}, {
  name: 'GPT-5.4',
  cost: 5.0,
  fill: '#00B4D8'
}, {
  name: 'Claude Haiku 4.5',
  cost: 0.25,
  fill: '#2A3B5C'
}, {
  name: 'GPT-5.4 mini',
  cost: 0.15,
  fill: '#00B4D8'
}, {
  name: 'Gemini 3.1 Flash-Lite',
  cost: 0.07,
  fill: '#10b981'
}].sort((a, b) => b.cost - a.cost);
const BUDGET_DATA = [{
  id: 'b1',
  name: 'Customer Support',
  allocated: 2500,
  consumed: 2125,
  trend: '+12%'
}, {
  id: 'b2',
  name: 'Content Generation',
  allocated: 1000,
  consumed: 920,
  trend: '+25%'
}, {
  id: 'b3',
  name: 'HR Automation',
  allocated: 1500,
  consumed: 675,
  trend: '-5%'
}, {
  id: 'b4',
  name: 'Internal Tools',
  allocated: 2000,
  consumed: 600,
  trend: '+2%'
} // 30%
];
// --- COMPONENTS ---
export function CostManagementDetailPage() {
  const [activeTab, setActiveTab] = useState('Panoramica');
  const [period, setPeriod] = useState('30G');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = useMemo(() => {
    return SERVICES_DATA.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.provider.toLowerCase().includes(searchQuery.toLowerCase()) || s.model.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-2220">
      {/* 1. HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-2221">
        <div data-id="element-2222">
          <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-2223">
            Cost Management
          </h1>
        </div>
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-2224">
          {['7G', '30G', '90G', '12M'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${period === p ? 'bg-[#F4F6F8] text-[#2A3B5C] shadow-sm' : 'text-gray-500 hover:text-[#1A202C]'}`} data-id="element-2225">
              {p}
            </button>)}
        </div>
      </div>

      {/* 2. KPI CARDS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-2226">
        {/* Card 1 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-2227">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-2228">
            Spesa Totale Mese
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-2229">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-2230">€4.832</span>
          </div>
        </div>

        {/* Card 2 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-2231">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-2232">
            Costo Medio/Giorno
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-2233">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-2234">€161</span>
          </div>
        </div>

        {/* Card 3 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-2235">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-2236">
            Forecast Fine Mese
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-2237">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-2238">€5.420</span>
          </div>
        </div>

        {/* Card 4 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-2239">
          <div className="flex justify-between items-center mb-3" data-id="element-2240">
            <h3 className="text-[13px] font-medium text-gray-500" data-id="element-2241">
              Budget Rimanente
            </h3>
            <span className="text-[11px] font-medium text-gray-400" data-id="element-2242">
              €7.000 tot
            </span>
          </div>
          <div className="mt-auto" data-id="element-2243">
            <div className="flex items-baseline gap-1 mb-2" data-id="element-2244">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-2245">
                €2.168
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-2246">
              <div className="bg-amber-500 h-1.5 rounded-full" style={{
              width: '69%'
            }} data-id="element-2247"></div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. TABS NAVIGATION */}
      <div className="border-b-2 border-gray-300 mb-6" data-id="element-2248">
        <div className="-mb-[2px] flex items-center gap-8 overflow-x-auto custom-scrollbar" data-id="element-2249">
          {['Panoramica', 'Per Servizio', 'Per Provider', 'Budget'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[15px] font-medium transition-colors whitespace-nowrap border-b-2 ${activeTab === tab ? 'border-[#00B4D8] text-[#2A3B5C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-2250">
                {tab}
              </button>)}
        </div>
      </div>

      {/* 4. TAB CONTENT */}

      {/* TAB 1: PANORAMICA */}
      {activeTab === 'Panoramica' && <div className="space-y-6" data-id="element-2251">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2252">
            {/* Area Chart */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-2253">
              <div className="flex justify-between items-center mb-6" data-id="element-2254">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2255">
                  Trend Costi Giornalieri
                </h2>
                <div className="flex items-center gap-4 text-[11px] font-medium" data-id="element-2256">
                  <div className="flex items-center gap-1.5" data-id="element-2257">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00B4D8]" data-id="element-2258"></div>
                    OpenAI
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-2259">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#2A3B5C]" data-id="element-2260"></div>
                    Anthropic
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-2261">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#10b981]" data-id="element-2262"></div>
                    Google
                  </div>
                </div>
              </div>
              <div className="h-[360px] w-full" data-id="element-2263">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2264">
                  <AreaChart data={DAILY_COSTS} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-2265">
                    <defs data-id="element-2266">
                      <linearGradient id="colorOpenAI" x1="0" y1="0" x2="0" y2="1" data-id="element-2267">
                        <stop offset="5%" stopColor="#00B4D8" stopOpacity={0.3} data-id="element-2268" />
                        <stop offset="95%" stopColor="#00B4D8" stopOpacity={0} data-id="element-2269" />
                      </linearGradient>
                      <linearGradient id="colorAnthropic" x1="0" y1="0" x2="0" y2="1" data-id="element-2270">
                        <stop offset="5%" stopColor="#2A3B5C" stopOpacity={0.3} data-id="element-2271" />
                        <stop offset="95%" stopColor="#2A3B5C" stopOpacity={0} data-id="element-2272" />
                      </linearGradient>
                      <linearGradient id="colorGoogle" x1="0" y1="0" x2="0" y2="1" data-id="element-2273">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-2274" />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-2275" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2276" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} dy={10} minTickGap={30} data-id="element-2277" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} tickFormatter={val => `€${val}`} data-id="element-2278" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  fontSize: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} itemStyle={{
                  color: '#1A202C',
                  fontWeight: 500
                }} formatter={(value: number, name: string) => [`€${value}`, name]} data-id="element-2279" />
                    <Area type="monotone" dataKey="Google" stackId="1" stroke="#10b981" fill="url(#colorGoogle)" strokeWidth={2} data-id="element-2280" />
                    <Area type="monotone" dataKey="Anthropic" stackId="1" stroke="#2A3B5C" fill="url(#colorAnthropic)" strokeWidth={2} data-id="element-2281" />
                    <Area type="monotone" dataKey="OpenAI" stackId="1" stroke="#00B4D8" fill="url(#colorOpenAI)" strokeWidth={2} data-id="element-2282" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Services */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col" data-id="element-2283">
              <div className="flex justify-between items-center mb-6" data-id="element-2284">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2285">
                  Top Servizi per Costo
                </h2>
                <button className="text-[12px] text-[#00B4D8] font-medium hover:underline" data-id="element-2286">
                  Vedi tutti
                </button>
              </div>
              <div className="flex-1 flex flex-col gap-4" data-id="element-2287">
                {SERVICES_DATA.slice(0, 5).map((service, idx) => {
              const totalCost = SERVICES_DATA.reduce((acc, curr) => acc + curr.cost, 0);
              const percent = (service.cost / totalCost * 100).toFixed(1);
              return <div key={service.id} className="flex items-center justify-between group" data-id="element-2288">
                      <div className="flex items-center gap-3 overflow-hidden" data-id="element-2289">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0" data-id="element-2290">
                          {idx + 1}
                        </div>
                        <div className="min-w-0" data-id="element-2291">
                          <p className="text-[13px] font-medium text-[#1A202C] truncate" data-id="element-2292">
                            {service.name}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate" data-id="element-2293">
                            {service.model}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 pl-2" data-id="element-2294">
                        <div className="w-16 hidden sm:block" data-id="element-2295">
                          <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[16px]" data-id="element-2296" />
                        </div>
                        <div className="text-right" data-id="element-2297">
                          <p className="text-[13px] font-semibold text-[#1A202C]" data-id="element-2298">
                            €{service.cost.toFixed(0)}
                          </p>
                          <p className="text-[10px] text-gray-400" data-id="element-2299">
                            {percent}%
                          </p>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>
          </div>

          {/* Anomalies & Suggestions */}
          <h2 className="text-[16px] font-semibold text-[#1A202C] mt-8 mb-4" data-id="element-2300">
            Anomalie & Suggerimenti
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2301">
            <div className="bg-red-50 border border-red-100 rounded-xl p-5 flex items-start gap-4" data-id="element-2302">
              <div className="p-2 bg-red-100 rounded-lg shrink-0 mt-0.5" data-id="element-2303">
                <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-2304" />
              </div>
              <div data-id="element-2305">
                <div className="flex items-center gap-2 mb-1" data-id="element-2306">
                  <h3 className="text-[14px] font-semibold text-red-900" data-id="element-2307">
                    Picco di spesa rilevato
                  </h3>
                  <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide" data-id="element-2308">
                    Azione richiesta
                  </span>
                </div>
                <p className="text-[13px] text-red-800/80 leading-relaxed mb-3" data-id="element-2309">
                  Il servizio <strong data-id="element-2310">Resume Screener</strong> ha registrato un
                  aumento dei costi del 42% negli ultimi 3 giorni, superando la
                  soglia di allerta.
                </p>
                <button className="text-[12px] font-medium text-red-700 hover:text-red-800 flex items-center gap-1" data-id="element-2311">
                  Analizza traffico <ArrowUpRight className="w-3.5 h-3.5" data-id="element-2312" />
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 flex items-start gap-4" data-id="element-2313">
              <div className="p-2 bg-blue-100 rounded-lg shrink-0 mt-0.5" data-id="element-2314">
                <Sparkles className="w-5 h-5 text-[#00B4D8]" data-id="element-2315" />
              </div>
              <div data-id="element-2316">
                <div className="flex items-center gap-2 mb-1" data-id="element-2317">
                  <h3 className="text-[14px] font-semibold text-blue-900" data-id="element-2318">
                    Suggerimento di ottimizzazione
                  </h3>
                  <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide" data-id="element-2319">
                    Risparmio stimato: €340/mese
                  </span>
                </div>
                <p className="text-[13px] text-blue-800/80 leading-relaxed mb-3" data-id="element-2320">
                  Il servizio <strong data-id="element-2321">Content Moderator</strong> utilizza
                  GPT-5.4. Passando a <strong data-id="element-2322">GPT-5.4 mini</strong> potresti
                  ridurre i costi dell'85% mantenendo performance simili per
                  questo task.
                </p>
                <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] flex items-center gap-1" data-id="element-2323">
                  Applica modifica <ArrowUpRight className="w-3.5 h-3.5" data-id="element-2324" />
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* TAB 2: PER SERVIZIO */}
      {activeTab === 'Per Servizio' && <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-2325">
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-2326">
            <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2327">
              Dettaglio Costi per Servizio
            </h2>
            <div className="relative w-full sm:w-72" data-id="element-2328">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2329" />
              <input type="text" placeholder="Cerca servizio, provider o modello..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] transition-all" data-id="element-2330" />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto" data-id="element-2331">
            <table className="w-full text-left border-collapse" data-id="element-2332">
              <thead data-id="element-2333">
                <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-2334">
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2335">
                    Servizio
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2336">
                    Provider & Modello
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2337">
                    Richieste
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2338">
                    Token
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2339">
                    Costo
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-2340">
                    Trend (7gg)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2341">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-2342">
                {filteredServices.map(service => <tr key={service.id} className="hover:bg-gray-50/50 transition-colors group cursor-pointer" data-id="element-2343">
                    <td className="px-6 py-4" data-id="element-2344">
                      <div className="flex items-center gap-3" data-id="element-2345">
                        <div className={`w-2 h-2 rounded-full ${service.status === 'danger' ? 'bg-red-500' : 'bg-emerald-500'}`} data-id="element-2346"></div>
                        <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2347">
                          {service.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-2348">
                      <div className="flex flex-col" data-id="element-2349">
                        <span className="text-[13px] text-[#1A202C] font-medium" data-id="element-2350">
                          {service.provider}
                        </span>
                        <span className="text-[11px] text-gray-500" data-id="element-2351">
                          {service.model}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2352">
                      <span className="text-[13px] text-gray-600" data-id="element-2353">
                        {service.requests}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2354">
                      <span className="text-[13px] text-gray-600" data-id="element-2355">
                        {service.tokens}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2356">
                      <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-2357">
                        €{service.cost.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 w-32" data-id="element-2358">
                      <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[24px]" data-id="element-2359" />
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2360">
                      <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] transition-colors opacity-0 group-hover:opacity-100" data-id="element-2361">
                        Dettagli
                      </button>
                    </td>
                  </tr>)}
                {filteredServices.length === 0 && <tr data-id="element-2362">
                    <td colSpan={7} className="px-6 py-8 text-center text-[13px] text-gray-500" data-id="element-2363">
                      Nessun servizio trovato per "{searchQuery}"
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden flex flex-col divide-y divide-gray-100" data-id="element-2364">
            {filteredServices.map(service => <div key={service.id} className="p-4 flex flex-col gap-3" data-id="element-2365">
                <div className="flex justify-between items-start" data-id="element-2366">
                  <div className="flex items-center gap-2" data-id="element-2367">
                    <div className={`w-2 h-2 rounded-full ${service.status === 'danger' ? 'bg-red-500' : 'bg-emerald-500'}`} data-id="element-2368"></div>
                    <div data-id="element-2369">
                      <p className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2370">
                        {service.name}
                      </p>
                      <p className="text-[11px] text-gray-500" data-id="element-2371">
                        {service.provider} • {service.model}
                      </p>
                    </div>
                  </div>
                  <span className="text-[14px] font-semibold text-[#1A202C]" data-id="element-2372">
                    €{service.cost.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1" data-id="element-2373">
                  <div className="w-24" data-id="element-2374">
                    <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[20px]" data-id="element-2375" />
                  </div>
                  <button className="text-[12px] font-medium text-[#00B4D8]" data-id="element-2376">
                    Dettagli
                  </button>
                </div>
              </div>)}
            {filteredServices.length === 0 && <div className="p-8 text-center text-[13px] text-gray-500" data-id="element-2377">
                Nessun servizio trovato per "{searchQuery}"
              </div>}
          </div>
        </div>}

      {/* TAB 3: PER PROVIDER */}
      {activeTab === 'Per Provider' && <div className="space-y-6" data-id="element-2378">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2379">
            {/* Donut Chart */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col" data-id="element-2380">
              <h2 className="text-[16px] font-semibold text-[#1A202C] mb-6" data-id="element-2381">
                Distribuzione Costi
              </h2>
              <div className="h-[320px] w-full relative flex-1" data-id="element-2382">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2383">
                  <PieChart data-id="element-2384">
                    <Pie data={PROVIDER_DATA} cx="50%" cy="50%" innerRadius={70} outerRadius={90} paddingAngle={2} dataKey="value" stroke="none" data-id="element-2385">
                      {PROVIDER_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2386" />)}
                    </Pie>
                    <Tooltip formatter={(value: number) => [`${value}%`, 'Quota']} contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-2387" />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" data-id="element-2388">
                  <span className="text-[12px] text-gray-500 font-medium" data-id="element-2389">
                    Totale
                  </span>
                  <span className="text-[24px] font-bold text-[#1A202C]" data-id="element-2390">
                    €3.853
                  </span>
                </div>
              </div>
            </div>

            {/* Provider Breakdown Cards */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2391">
              {PROVIDER_DATA.map(provider => <div key={provider.name} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col" data-id="element-2392">
                  <div className="flex items-center gap-2 mb-4" data-id="element-2393">
                    <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: provider.color
              }} data-id="element-2394"></div>
                    <h3 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-2395">
                      {provider.name}
                    </h3>
                  </div>
                  <div className="mb-4" data-id="element-2396">
                    <p className="text-[24px] font-bold text-[#1A202C]" data-id="element-2397">
                      €{provider.cost.toFixed(2)}
                    </p>
                    <p className={`text-[12px] font-medium mt-1 ${provider.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`} data-id="element-2398">
                      {provider.trend} vs mese prec.
                    </p>
                  </div>
                  <div className="mt-auto space-y-2 pt-4 border-t border-gray-100" data-id="element-2399">
                    <div className="flex justify-between items-center" data-id="element-2400">
                      <span className="text-[12px] text-gray-500" data-id="element-2401">
                        Modelli attivi
                      </span>
                      <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-2402">
                        {provider.models}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-2403">
                      <span className="text-[12px] text-gray-500" data-id="element-2404">
                        Modello top
                      </span>
                      <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-2405">
                        {provider.topModel}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Cost per 1k Tokens Bar Chart */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-2406">
            <div className="flex justify-between items-center mb-6" data-id="element-2407">
              <div data-id="element-2408">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2409">
                  Costo per 1M Token (Input + Output mix)
                </h2>
                <p className="text-[12px] text-gray-500 mt-1" data-id="element-2410">
                  Confronto prezzi di listino dei modelli utilizzati
                </p>
              </div>
            </div>
            <div className="h-[360px] w-full" data-id="element-2411">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2412">
                <BarChart data={TOKEN_COST_DATA} layout="vertical" margin={{
              top: 0,
              right: 30,
              left: 40,
              bottom: 0
            }} data-id="element-2413">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" data-id="element-2414" />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6B7280'
              }} tickFormatter={val => `€${val}`} data-id="element-2415" />
                  <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#1A202C',
                fontWeight: 500
              }} width={120} data-id="element-2416" />
                  <Tooltip cursor={{
                fill: '#F3F4F6'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} formatter={(value: number) => [`€${value.toFixed(2)}`, 'Costo / 1M Token']} data-id="element-2417" />
                  <Bar dataKey="cost" radius={[0, 4, 4, 0]} barSize={24} data-id="element-2418">
                    {TOKEN_COST_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-2419" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>}

      {/* TAB 4: BUDGET */}
      {activeTab === 'Budget' && <div className="space-y-6" data-id="element-2420">
          {/* Alert Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-id="element-2421">
            <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-2422" />
            <div data-id="element-2423">
              <h3 className="text-[14px] font-semibold text-amber-900" data-id="element-2424">
                Attenzione ai limiti di budget
              </h3>
              <p className="text-[13px] text-amber-800/80 mt-1" data-id="element-2425">
                Le linee di budget <strong data-id="element-2426">Content Generation</strong> e{' '}
                <strong data-id="element-2427">Customer Support</strong> hanno superato l'80%
                dell'allocazione mensile. Potrebbe essere necessario un
                ribilanciamento.
              </p>
            </div>
          </div>

          {/* Budget Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2428">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between" data-id="element-2429">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2430">
                Stato Linee di Budget
              </h2>
              <button className="text-[13px] text-[#00B4D8] font-medium hover:underline" data-id="element-2431">
                Gestisci Allocazioni
              </button>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto" data-id="element-2432">
              <table className="w-full text-left border-collapse" data-id="element-2433">
                <thead data-id="element-2434">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-2435">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2436">
                      Linea di Budget
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2437">
                      Allocato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2438">
                      Consumato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2439">
                      Rimanente
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-64" data-id="element-2440">
                      % Utilizzo
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-2441">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2442">
                  {BUDGET_DATA.map(budget => {
                const percent = budget.consumed / budget.allocated * 100;
                const remaining = budget.allocated - budget.consumed;
                let statusColor = 'bg-emerald-500';
                let badgeClass = 'bg-emerald-50 text-emerald-600 border-emerald-100';
                let statusText = 'In linea';
                if (percent >= 90) {
                  statusColor = 'bg-red-500';
                  badgeClass = 'bg-red-50 text-red-600 border-red-100';
                  statusText = 'Critico';
                } else if (percent >= 70) {
                  statusColor = 'bg-amber-500';
                  badgeClass = 'bg-amber-50 text-amber-600 border-amber-100';
                  statusText = 'Attenzione';
                }
                return <tr key={budget.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-2443">
                        <td className="px-6 py-4" data-id="element-2444">
                          <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2445">
                            {budget.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2446">
                          <span className="text-[13px] text-gray-600" data-id="element-2447">
                            {formatCurrency(budget.allocated)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2448">
                          <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-2449">
                            {formatCurrency(budget.consumed)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2450">
                          <span className="text-[13px] text-gray-600" data-id="element-2451">
                            {formatCurrency(remaining)}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-2452">
                          <div className="flex items-center gap-3" data-id="element-2453">
                            <div className="flex-1 bg-gray-100 rounded-full h-2" data-id="element-2454">
                              <div className={`${statusColor} h-2 rounded-full`} style={{
                          width: `${Math.min(percent, 100)}%`
                        }} data-id="element-2455"></div>
                            </div>
                            <span className="text-[12px] font-medium text-gray-600 w-9 text-right" data-id="element-2456">
                              {percent.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-2457">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${badgeClass}`} data-id="element-2458">
                            {statusText}
                          </span>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col divide-y divide-gray-100" data-id="element-2459">
              {BUDGET_DATA.map(budget => {
            const percent = budget.consumed / budget.allocated * 100;
            let statusColor = 'bg-emerald-500';
            let badgeClass = 'bg-emerald-50 text-emerald-600 border-emerald-100';
            let statusText = 'In linea';
            if (percent >= 90) {
              statusColor = 'bg-red-500';
              badgeClass = 'bg-red-50 text-red-600 border-red-100';
              statusText = 'Critico';
            } else if (percent >= 70) {
              statusColor = 'bg-amber-500';
              badgeClass = 'bg-amber-50 text-amber-600 border-amber-100';
              statusText = 'Attenzione';
            }
            return <div key={budget.id} className="p-4 flex flex-col gap-3" data-id="element-2460">
                    <div className="flex justify-between items-start" data-id="element-2461">
                      <p className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2462">
                        {budget.name}
                      </p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium border ${badgeClass}`} data-id="element-2463">
                        {statusText}
                      </span>
                    </div>
                    <div data-id="element-2464">
                      <div className="flex justify-between text-[11px] text-gray-500 mb-1.5" data-id="element-2465">
                        <span data-id="element-2466">
                          {formatCurrency(budget.consumed)} /{' '}
                          {formatCurrency(budget.allocated)}
                        </span>
                        <span className="font-medium text-gray-700" data-id="element-2467">
                          {percent.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-2468">
                        <div className={`${statusColor} h-1.5 rounded-full`} style={{
                    width: `${Math.min(percent, 100)}%`
                  }} data-id="element-2469"></div>
                      </div>
                    </div>
                  </div>;
          })}
            </div>
          </div>
        </div>}
    </div>;
}